import IconView from "@mui/icons-material/RemoveRedEye";
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewAuthor,
  fetchAuthors,
  resetStateAuthors,
  updateAuthor,
} from "../../app/slices/authorSlice";
import { getObjectDifference } from "../../app/utils";
import EmptyDataContainer from "../../components/emptyDataContainer/emptyDataContainer";
import ErrorContainer from "../../components/errorContainer/errorContainer";
import LoadingContainer from "../../components/loadingContainer/loadingContainer";
import AuthorDetails from "./AuthorDetails";

const AuthorList = () => {
  const { list, error, status } = useSelector(({ authors }) => authors.get);
  const dispatch = useDispatch();

  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [open, setOpen] = useState(false);
  const [authors, setAuthors] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const columns = [
    { name: "IMAGE", label: "Image" },
    { name: "NAME", label: "Author Name" },
    { name: "STATUSDISPLAY", label: "Status" },
    { name: "ACTION", label: "Action", align: "center" },
  ];

  const translateStatus = (status) => {
    switch (status) {
      case "A":
        return "Active";
      case "I":
        return "Inactive";
      case "D":
        return "Disabled";
      default:
        return "N/A";
    }
  };

  useEffect(() => {
    dispatch(fetchAuthors());
  }, [dispatch]);

  useEffect(() => {
    if (list?.length > 0) {
      const formattedList = list.map((author) => ({
        ...author,
        STATUSDISPLAY: translateStatus(author.STATUS).toUpperCase(),
      }));
      setAuthors(formattedList);
    }
  }, [list]);

  const handleClose = () => {
    setSelectedAuthor(null);
    setFormValues({});
    dispatch(fetchAuthors());
    setOpen(false);
  };

  const handleSubmit = async (data) => {
    dispatch(resetStateAuthors(["post", "put"]));
    let _payload = {
      NAME: data.name,
      BIOGRAPHY: data.biography,
      DATE_OF_BIRTH: data.dateOfBirth,
      PROFILE_PICTURE: data.newImage || formValues?.profileImage,
    };

    if (selectedAuthor) {
      const initialPayload = {
        NAME: selectedAuthor.NAME || "",
        BIOGRAPHY: selectedAuthor.BIOGRAPHY || "",
        DATE_OF_BIRTH: selectedAuthor.DATE_OF_BIRTH || "",
      };
      const currentPayload = {
        NAME: data.name || "",
        BIOGRAPHY: data.biography || "",
        DATE_OF_BIRTH: data.dateOfBirth || "",
      };
      const diff = getObjectDifference(initialPayload, currentPayload);
      if (data.newImage) diff.PROFILE_PICTURE = data.newImage;

      dispatch(
        updateAuthor({
          id: selectedAuthor.AUTHOR_ID,
          payload: diff,
        })
      );
    } else {
      dispatch(addNewAuthor(_payload));
    }
  };

  const filteredAuthors = authors?.filter((author) =>
    author.NAME.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedAuthors = filteredAuthors?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const renderTable = () => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.name}
                sx={{ fontWeight: "bold", textAlign: column?.align || "left" }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedAuthors?.map((author) => (
            <TableRow key={author.ID}>
              <TableCell>
                <Avatar
                  alt={author.NAME}
                  src={author.PROFILE_PICTURE || "/default-avatar.png"}
                  sx={{ width: 50, height: 50 }}
                />
              </TableCell>
              <TableCell>{author.NAME}</TableCell>
              <TableCell>{author.STATUSDISPLAY}</TableCell>

              <TableCell sx={{ textAlign: "center" }}>
                <IconButton
                  color="secondary"
                  onClick={() => {
                    setSelectedAuthor(author);
                    setOpen(true);
                  }}
                >
                  <IconView />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          color="primary"
          variant="contained"
          onClick={() => setOpen(true)}
        >
          Register Author
        </Button>

        <TextField
          label="Search Authors"
          variant="outlined"
          sx={{ mb: 2 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {status === "loading" && (
        <LoadingContainer
          hasBackground={true}
          message="Loading authors..."
          id="loading-container-authors"
        />
      )}

      {error && status !== "loading" && (
        <ErrorContainer
          id="error-container-authors"
          hasBackground={true}
          message={error?.message || "An error occurred"}
          onReload={() => {
            dispatch(resetStateAuthors());
            dispatch(fetchAuthors());
          }}
        />
      )}

      {status === "idle" && list?.length === 0 && (
        <EmptyDataContainer
          message="No authors found"
          hasBackground={true}
          id="empty-data-authors"
        />
      )}

      {list?.length > 0 && error === null && status === "idle" && renderTable()}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
          marginBottom: "20px",
        }}
      >
        <Pagination
          count={Math.ceil(filteredAuthors?.length / itemsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </div>

      {open && (
        <Dialog
          open={open}
          fullWidth
          maxWidth="sm"
          sx={{
            "& .MuiDialog-paper": {
              maxHeight: "90vh",
            },
          }}
          aria-labelledby="dialog-title"
          aria-describedby="dialog-description"
        >
          <DialogContent sx={{ maxHeight: "80vh", overflowY: "auto" }}>
            <AuthorDetails
              selectedAuthor={selectedAuthor}
              onSave={handleSubmit}
              formValues={formValues}
              setFormValues={setFormValues}
              handleClose={handleClose}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AuthorList;
